import withStyles from 'react-jss';
import React, { useState, useEffect } from 'react';
import { Drawer, Tabs } from 'antd';
import { CssType, ThemeType } from '@theming/jssTypes';
const helpIcon = require('@assets/images/virtual-assistant.svg');
const { TabPane } = Tabs;
import {
  ArrowRightOutlined,
  GiftOutlined,
  QuestionCircleOutlined,
  LinkOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import Loader from 'src/design-system/components/molecules/Loader/Loader';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { generateFrillSSOToken } from '@api/dashboard';
import { LocalStorageKeys, LocalStorageService } from 'src/services/localStorage';

const productUrl = 'https://shipsy.frill.co';
const helpUrl = 'https://shipsy-helpdesk.refined.site/space/HELP';

const styles = (theme: ThemeType): CssType => ({
  drawer: {
    '& .ant-drawer-body': {
      padding: 0,
    },
    '& .ant-tabs': {
      backgroundColor: theme.colors.globalHeader,
      color: theme.colors.textOnDarkBg,
      height: '100%',
    },
    '& .ant-tabs-content': {
      height: '100%',
    },
    '& .ant-drawer-header': {
      backgroundColor: theme.colors.globalHeader,
      '& .anticon svg': {
        color: theme.colors.textOnDarkBg,
      }
    },
    '& .ant-drawer-title': {
      color: theme.colors.textOnDarkBg,
    }
  },
  tabpane: {
    padding: '5px',
  },
  iframeContainer: {
    height: '100%',
  },
  iframe: {
    height: '100%',
    width: '100%',
    border: 'none',
  },
  viewAll: {
    fontSize: theme.sizes.bodyText,
    position: 'absolute',
    top: '120px',
    right: '5px',
  },
  help: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpText: {
    color: theme.colors.primaryColor,
    fontSize: theme.sizes.bodyTextLarge,
  },
  helpLink: {
    fontSize: theme.sizes.heading,
  },
  tabContainer: {
    '& .ant-tabs-nav-list': {
      width: '100%',
    },
    '& .ant-tabs-tab': {
      width: '50%',
      justifyContent: 'center',
    },
  },
  helpHeaderStyle: {
    backgroundColor: theme.colors.globalHeader,
  }
});

const HelpCenterDrawer = (props: any) => {
  const { t } = useTranslation();
  const { classes, isVisible, onModalClose } = props;
  const [loading, setLoading] = useState(true);
  const [frillUrl, setFrillUrl] = useState<string>(productUrl);
  useEffect(() => {
    const fetchSSOToken = async () => {
      if (!isVisible) return;
      setLoading(true);
      const organisationId = await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__);
      if (!organisationId) {
        setFrillUrl(productUrl);
        setLoading(false);
        return;
      }
      const response = await generateFrillSSOToken(organisationId);
      if (response?.isSuccess && response?.data?.token) {
        const token = response.data.token;
        const urlWithToken = `${productUrl}/login/sso?ssoToken=${token}`;
        setFrillUrl(urlWithToken);
      } else {
        if (!response?.isSuccess) {
          console.error('Error fetching Frill SSO token:', response?.errorMessage);
        }
        setFrillUrl(productUrl);
        setLoading(false);
      }
    };
    fetchSSOToken();
  }, [isVisible]);

  const renderUpdates = () => {
    return (
      <div className={classes.iframeContainer}>
        <div className={classes.viewAll}>
          <a target="_blank" href={frillUrl}>
            {t('View All')} <ArrowRightOutlined />
          </a>
        </div>
        <iframe
          title={t('Product Board')}
          className={classes.iframe}
          src={frillUrl}
          onLoad={() => setLoading(false)}
        ></iframe>
        {loading ? <Loader /> : null}
      </div>
    );
  };

  const renderHelp = () => {
    return (
      <div className={classes.help}>
        <div>
          <img src={helpIcon} />
        </div>
        <div className={classes.helpText}>
          {t('Detailed step-by-step guide available on')}
        </div>
        <div className={classes.helpLink}>
          <a target="_blank" href={helpUrl}>
            {t('Help Centre')} <LinkOutlined />
          </a>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      width={isMobile ? "80%" : "30%"}
      visible={isVisible}
      closable={false}
      onClose={onModalClose}
      title={t('Updates and Help Centre')}
      className={classes.drawer}
      extra={<CloseOutlined onClick={onModalClose} />}
    >
      <Tabs
        defaultActiveKey={'updates'}
        type="line"
        {...props}
        className={classes.tabContainer}
      >
        <TabPane
          tab={
            <span className={classes.tabpane}>
              <GiftOutlined />
              {t('Whats New')}
            </span>
          }
          key={'updates'}
        >
          {renderUpdates()}
        </TabPane>
        <TabPane
          tab={
            <span className={classes.tabpane}>
              <QuestionCircleOutlined />
              {t('Help Centre')}
            </span>
          }
          key={'help'}
        >
          {renderHelp()}
        </TabPane>
      </Tabs>
    </Drawer>
  );
};

export default withStyles(styles, { injectTheme: true })(HelpCenterDrawer);
